import React, { useState } from 'react';
import usePWAInstall from '../../hooks/usePWAInstall';
import PWAInstallInstructions from './PWAInstallInstructions';

const DownloadIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

// Reusable "Download / Install app" button.
//
// variants:
//   'icon'       - bare icon button (light surfaces, e.g. mobile menubar)
//   'light'      - bare icon button for dark surfaces
//   'pill'       - icon + label pill on light surfaces (desktop navbar/header)
//   'pill-light' - icon + label pill for dark surfaces (mobile hero)
//   'block'      - full-width prominent button (mobile landing CTA)
const PWAInstallButton = ({ variant = 'pill', label = 'Download App', className = '' }) => {
  const { canPrompt, isIOS, isStandalone, isInstalled, promptInstall } = usePWAInstall();
  const [showInstructions, setShowInstructions] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);

  // Only hide once the app is actually installed / running standalone.
  // Otherwise always show the button: if the native prompt isn't available
  // yet, clicking falls back to manual "Add to Home Screen" instructions.
  if (isStandalone || isInstalled) return null;

  const handleClick = async () => {
    if (canPrompt) {
      const outcome = await promptInstall();
      if (outcome === 'accepted') {
        setShowSnackbar(true);
        setTimeout(() => setShowSnackbar(false), 4000);
      } else if (outcome === 'unavailable') {
        setShowInstructions(true);
      }
      return;
    }
    // iOS or prompt not available -> show manual instructions.
    setShowInstructions(true);
  };

  const variantClasses = {
    icon:
      'inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#f1f5f4] text-slate-800 ring-1 ring-slate-200/90 transition-colors hover:text-[#0f766e] focus:outline-none focus:ring-2 focus:ring-[#0f766e]',
    light:
      'inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none',
    pill:
      'inline-flex items-center gap-2 rounded-full border border-[#d7eae4] bg-white px-3.5 py-2 text-sm font-semibold text-[#0f766e] shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#f0fbf8] focus:outline-none focus:ring-2 focus:ring-[#0f766e] focus:ring-offset-2',
    'pill-light':
      'inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20 focus:outline-none',
    block:
      'touch-target-lg inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-full bg-[#facc15] px-6 text-base font-bold text-[#052e2b] shadow-[0_20px_50px_-28px_rgba(0,0,0,0.45)] transition-transform duration-200 hover:scale-[1.01] focus:outline-none',
  };

  const isIconOnly = variant === 'icon' || variant === 'light';

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`${variantClasses[variant] || variantClasses.pill} ${className}`.trim()}
        title="Install UttaApp"
        aria-label="Install UttaApp"
        style={{ pointerEvents: 'auto' }}
      >
        <DownloadIcon className={isIconOnly ? 'w-5 h-5' : 'w-5 h-5'} />
        {!isIconOnly && <span>{label}</span>}
      </button>

      {showInstructions && (
        <PWAInstallInstructions isIOS={isIOS} onClose={() => setShowInstructions(false)} />
      )}

      {showSnackbar && (
        <div className="fixed top-5 left-1/2 z-[10002] w-[90%] max-w-md -translate-x-1/2">
          <div className="flex items-center justify-between rounded-xl bg-[#0f766e] px-4 py-3.5 text-white shadow-2xl">
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm font-semibold">UttaApp installed! Find it on your home screen.</span>
            </div>
            <button onClick={() => setShowSnackbar(false)} className="ml-3 shrink-0 text-white/90 hover:text-white" aria-label="Close">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallButton;
